import { useState } from 'react';
import type { AppState, Entry } from '../../shared/api';
import { DUPLICATE_ENTRY_MESSAGE } from '../../shared/catalog';
import { CanImage } from '../components/CanImage';
import { Sheet } from '../components/Sheet';
import { VariantPicker } from '../components/VariantPicker';
import { useAddEntry, useDeleteEntry, useUpdateEntry } from '../api';
import { formatDate } from '../format';

export interface AddDraft {
  canId: number;
  country: string;
  sizeMl: number;
  ean?: string;
  note?: string;
  scanned?: boolean;
}

interface DetailProps {
  state: AppState;
  entry: Entry;
  onClose: () => void;
  onError: (message: string) => void;
}

/** Blik detail: an entry on the wall. Country and size save immediately. */
export function DetailSheet({ state, entry, onClose, onError }: DetailProps) {
  const can = state.cans.find((c) => c.id === entry.canId);
  const category = state.categories.find((c) => c.id === can?.categoryId);
  const variants = state.entries.filter((e) => e.canId === entry.canId).length;
  const update = useUpdateEntry();
  const remove = useDeleteEntry();
  const [confirming, setConfirming] = useState(false);

  const set = (body: { country?: string; sizeMl?: number }) =>
    update.mutate({ id: entry.id, ...body }, { onError: (err) => onError(err.message) });

  return (
    <Sheet label={can?.name ?? 'Blik'} onClose={onClose}>
      {(close) => (
        <>
          <div className="sheet__top">
            <div className="sheet__img">
              <CanImage imageId={can?.imageId} alt={can?.name} />
            </div>
            <div className="sheet__head">
              <div className="label label--green">
                {category?.name} · {variants} {variants === 1 ? 'variant' : 'varianten'}
              </div>
              <h2 className="sheet__name">{can?.name}</h2>
              <div className="sheet__meta">
                {entry.ean && (
                  <>
                    EAN {entry.ean}
                    <br />
                  </>
                )}
                Toegevoegd {formatDate(entry.addedAt)}
              </div>
            </div>
          </div>

          <VariantPicker
            country={entry.country}
            sizeMl={entry.sizeMl}
            onCountry={(country) => set({ country })}
            onSize={(sizeMl) => set({ sizeMl })}
          />

          {confirming ? (
            <>
              <div className="note">Weet je het zeker? Deze variant gaat uit je collectie.</div>
              <div className="sheet-actions" style={{ marginTop: 12 }}>
                <button type="button" className="btn-danger" style={{ color: 'var(--text-2)', borderColor: 'var(--outline)' }} onClick={() => setConfirming(false)}>
                  Annuleer
                </button>
                <button
                  type="button"
                  className="btn-done btn-done--danger"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(entry.id, {
                      onSuccess: close,
                      onError: (err) => onError(err.message),
                    })
                  }
                >
                  Verwijder uit collectie
                </button>
              </div>
            </>
          ) : (
            <div className="sheet-actions">
              <button type="button" className="btn-danger" onClick={() => setConfirming(true)}>
                Verwijder
              </button>
              <button type="button" className="btn-done" onClick={close}>
                Klaar
              </button>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

interface AddProps {
  state: AppState;
  draft: AddDraft;
  onClose: () => void;
  onAdded: (entry: Entry) => void;
  onError: (message: string) => void;
}

/** The add sheet: same layout as the detail, without image and delete. */
export function AddSheet({ state, draft, onClose, onAdded, onError }: AddProps) {
  const can = state.cans.find((c) => c.id === draft.canId);
  const category = state.categories.find((c) => c.id === can?.categoryId);
  const owned = state.entries.filter((e) => e.canId === draft.canId).length;
  const [country, setCountry] = useState(draft.country);
  const [sizeMl, setSizeMl] = useState(draft.sizeMl);
  const add = useAddEntry();
  const duplicate = state.entries.some(
    (entry) => entry.canId === draft.canId && entry.country === country && entry.sizeMl === sizeMl,
  );

  return (
    <Sheet label={`${can?.name ?? 'Blik'} toevoegen`} onClose={onClose}>
      {(close) => (
        <>
          <div className="sheet__top">
            <div className="sheet__head">
              <div className="label label--green">
                {draft.scanned ? `Gescand · ${category?.name}` : `${category?.name} · Toevoegen`}
              </div>
              <h2 className="sheet__name">{can?.name}</h2>
              <div className="sheet__meta">
                {draft.ean && (
                  <>
                    EAN {draft.ean}
                    <br />
                  </>
                )}
                {/* The scan note already says how many you already have. */}
                {!draft.note && (owned ? `Al ${owned}× in je collectie` : 'Nog niet in je collectie')}
              </div>
            </div>
          </div>
          {draft.note && <div className="note">{draft.note}</div>}

          <VariantPicker country={country} sizeMl={sizeMl} onCountry={setCountry} onSize={setSizeMl} />

          <div role="status">
            {duplicate && (
              <div className="note">
                {DUPLICATE_ENTRY_MESSAGE} Kies een ander land of een andere inhoud voor een nieuwe variant.
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn"
            style={{ marginTop: 24 }}
            disabled={add.isPending}
            onClick={() => {
              if (duplicate) return onError(DUPLICATE_ENTRY_MESSAGE);
              add.mutate(
                { canId: draft.canId, country, sizeMl, ean: draft.ean },
                {
                  onSuccess: (entry) => {
                    close();
                    onAdded(entry);
                  },
                  onError: (err) => onError(err.message),
                },
              );
            }}
          >
            In collectie
          </button>
        </>
      )}
    </Sheet>
  );
}
